const PhilosophySection = () => {
  const principles = [
    {
      title: 'Excellence Through Discipline',
      quote: 'Success is the sum of small efforts repeated day in and day out.',
      description: 'Whether practicing chess tactics for hours or refining robot code through dozens of iterations, I believe that consistent, disciplined effort is the foundation of mastery.',
    },
    {
      title: 'Growth Through Challenges',
      quote: 'Every setback is a setup for a comeback.',
      description: 'Losses in chess tournaments and failed robot prototypes aren\'t failures—they\'re lessons. Each challenge teaches resilience and reveals new paths to improvement.',
    },
    {
      title: 'Leadership Through Service',
      quote: 'True leadership is measured by how many people you help succeed.',
      description: 'From mentoring young chess players to leading robotics workshops, I\'ve learned that the best leaders empower others to reach their full potential.',
    },
    {
      title: 'Innovation Through Collaboration',
      quote: 'None of us is as smart as all of us.',
      description: 'The greatest innovations happen when diverse perspectives unite toward a common goal. My robotics team victories and community projects prove that collaboration amplifies individual talents.',
    },
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-8 sm:mb-12">
          <h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-bold mb-3 sm:mb-4"
            style={{ color: 'var(--color-heading)' }}
          >
            My Philosophy
          </h2>
          <p
            className="text-base sm:text-lg max-w-3xl mx-auto"
            style={{ color: 'var(--color-subtle)' }}
          >
            Core principles that guide my approach to life, learning, and leadership.
          </p>
        </div>

        {/* Mobile: horizontal scroll, Desktop: grid */}
        <div className="md:grid md:grid-cols-2 md:gap-6 flex md:flex-none overflow-x-auto gap-4 snap-x snap-mandatory scrollbar-hide pb-4 md:pb-0 -mx-4 px-4 md:mx-0">
          {principles.map((principle, index) => (
            <div
              key={index}
              className="p-5 sm:p-6 rounded-lg transition-all duration-300 hover:shadow-card-hover flex-shrink-0 w-[85vw] md:w-auto snap-center"
              style={{
                backgroundColor: 'var(--color-card-bg)',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'var(--color-card-border)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-card-hover-border)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--color-card-border)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <h3
                className="text-lg sm:text-xl font-bold mb-2 sm:mb-3"
                style={{ color: 'var(--color-heading)' }}
              >
                {principle.title}
              </h3>

              <div
                className="mb-3 sm:mb-4 pl-3 sm:pl-4 border-l-4 py-1"
                style={{ borderColor: 'var(--color-accent-5)' }}
              >
                <p
                  className="italic text-base sm:text-lg font-medium"
                  style={{ color: 'var(--color-accent-7)' }}
                >
                  "{principle.quote}"
                </p>
              </div>

              <p
                className="leading-relaxed text-sm sm:text-base"
                style={{ color: 'var(--color-subtle)' }}
              >
                {principle.description}
              </p>
            </div>
          ))}
        </div>

        {/* Personal Motto */}
        <div
          className="mt-8 sm:mt-12 p-6 sm:p-8 rounded-xl text-center"
          style={{
            background: 'linear-gradient(135deg, var(--color-accent-1), var(--color-accent-2))',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'var(--color-accent-3)',
          }}
        >
          <p
            className="text-xl sm:text-2xl md:text-3xl font-bold italic mb-2"
            style={{ color: 'var(--color-accent-8)' }}
          >
            "Think strategically. Build collaboratively. Lead compassionately."
          </p>
          <p
            className="text-sm sm:text-base"
            style={{ color: 'var(--color-accent-7)' }}
          >
            My personal motto that unites chess, robotics, and social impact
          </p>
        </div>
      </div>
    </section>
  );
};

export default PhilosophySection;
